import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { useEffect, useState } from "react";
import {
    Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2,
    List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
    Undo, Redo, Code2, Eraser,
} from "lucide-react";

function Btn({ onClick, active, disabled, title, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            data-testid={testid}
            className={
                "p-2 border-r border-[color:var(--espresso)] transition-colors disabled:opacity-30 " +
                (active ? "bg-[color:var(--gold)] text-[color:var(--warm-black)]" : "text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]")
            }
        >
            {children}
        </button>
    );
}

export default function RichTextEditor({ value, onChange, testid = "campaign-body-editor" }) {
    const [rawMode, setRawMode] = useState(false);
    const [rawDraft, setRawDraft] = useState(value || "");
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true, HTMLAttributes: { style: "color:#c9a24a;text-decoration:underline" } }),
            Image.configure({ HTMLAttributes: { style: "max-width:100%;height:auto;display:block;margin:16px auto" } }),
        ],
        content: value || "",
        onUpdate: ({ editor: e }) => onChange(e.getHTML()),
        editorProps: {
            attributes: {
                class: "min-h-[240px] p-4 focus:outline-none text-[color:var(--soft-cream)] prose prose-invert max-w-none",
                "data-testid": `${testid}-surface`,
            },
        },
    });

    // Keep editor in sync when a template loads a new value externally.
    useEffect(() => {
        if (!editor) return;
        if (rawMode) return;
        const current = editor.getHTML();
        if ((value || "") !== current) {
            editor.commands.setContent(value || "", false);
        }
    }, [value, editor, rawMode]);

    // When flipping to raw mode, seed the textarea from the editor.
    useEffect(() => {
        if (rawMode) setRawDraft(value || "");
    }, [rawMode, value]);

    const setLink = () => {
        const prior = editor?.getAttributes("link").href;
        const url = window.prompt("Link URL (leave blank to remove)", prior || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const addImage = () => {
        const url = window.prompt("Image URL (paste a hosted image link)", "https://");
        if (!url) return;
        editor.chain().focus().setImage({ src: url }).run();
    };

    if (!editor) return null;

    return (
        <div className="border border-[color:var(--espresso)] mb-3" data-testid={testid}>
            <div className="flex flex-wrap items-center border-b border-[color:var(--espresso)] bg-[color:var(--warm-black)]/60">
                <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="rte-bold"><Bold size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="rte-italic"><Italic size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testid="rte-underline"><UnderlineIcon size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1" testid="rte-h1"><Heading1 size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2" testid="rte-h2"><Heading2 size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testid="rte-bullet"><List size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ordered"><ListOrdered size={14} /></Btn>
                <Btn onClick={setLink} active={editor.isActive("link")} title="Insert / edit link" testid="rte-link"><LinkIcon size={14} /></Btn>
                <Btn onClick={addImage} title="Insert image by URL" testid="rte-image"><ImageIcon size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testid="rte-clear"><Eraser size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="rte-undo"><Undo size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="rte-redo"><Redo size={14} /></Btn>
                <div className="ml-auto">
                    <Btn
                        onClick={() => {
                            if (rawMode) {
                                onChange(rawDraft);
                                setRawMode(false);
                            } else {
                                setRawMode(true);
                            }
                        }}
                        active={rawMode}
                        title="Toggle raw HTML"
                        testid="rte-raw"
                    >
                        <Code2 size={14} />
                    </Btn>
                </div>
            </div>
            {rawMode ? (
                <textarea
                    className="w-full min-h-[240px] p-4 bg-transparent border-0 focus:outline-none font-mono text-xs text-[color:var(--soft-cream)]"
                    value={rawDraft}
                    onChange={(e) => setRawDraft(e.target.value)}
                    onBlur={() => onChange(rawDraft)}
                    placeholder="Paste raw HTML here. Toggle the code button off to return to the visual editor."
                    data-testid={`${testid}-raw`}
                />
            ) : (
                <EditorContent editor={editor} />
            )}
        </div>
    );
}
